import React from "react";
import { change, Field, FieldArray, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { get } from "lodash";
import { Col, Button } from "react-bootstrap";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { NoteTooltip } from "MODULES/shared/components/NoteTooltip";
import {
  CONSIGNEE,
  CONSIGNEE_TYPES,
  EIN,
  REPAIR_ORDER_CONSIGNEE_TYPES,
  ROUTED_EXPORT,
  USPPI
} from "MODULES/Shipping/constants";
import { toOptions } from "LIB/forms";
import api from "APP_ROOT/api";
import Warning from "MODULES/shared/components/Warning/Warning";
import {
  form,
  Form as AppForm,
  FormInput,
  Select,
  Errors,
  TextArea
} from "MODULES/shared/components/Forms";
import { RESTRICTED_COUNTRIES } from "LIB/countries";
import { API_BASE, CUSTOMER_PO, LINE_ITEMS } from "APP_ROOT/constants";
import Save from "MODULES/shared/components/CommercialInvoice/Form/Save";
import { HAZMAT } from "MODULES/parts/constants";
import { properCase } from "LIB/formatters/text";
import {
  SAVE_OR_REPLACE_FORM,
  SHIPPING_CHECKLIST
} from "MODULES/shared/components/CommercialInvoice/helpers";
import { CARGO_SCREENING_CONSENT } from "MODULES/shared/components/CommercialInvoice/constants";
import { EXTERNAL_NOTE } from "MODULES/PurchaseOrders/constants";
import validate from "./FormSections/validate";
import AddressFields from "./FormSections/AddressFields";
import FreightForwarderAddressFields from "./FormSections/FreightForwarderAddressFields";
import Contact from "./FormSections/Contact";
import PartDetails from "./FormSections/PartDetails";
import InvoiceWeight from "./FormSections/InvoiceWeight";

const usppiOptions = routedExport =>
  [
    { value: true, text: "Seller is USPPI" },
    {
      value: false,
      text:
        "Cust. will need to contact Acting Freight Forwarder and authorize by email or POA"
    }
  ].sort(() => (routedExport ? -1 : 0));

const options = [{ value: true, text: "Yes" }, { value: false, text: "No" }];

const consigneeNote = `<div style="margin:10px"><div>&bull;Direct Consumer - a non-government institution, enterprise, or company that will consume or use the exported good as a consumable, for its own internal processes, as an input to the production.</div><br />
<div>&bull;Government Entity - a government-owned or government-controlled agency, institution, enterprise, or company.</div><br />
<div>&bull;Reseller - a non-government reseller, retailer, wholesaler, distributor, distribution center or trading company.</div><br />
<div>&bull;Other/Unknown - an entity not previously indicated, as defined above, or whose ultimate consignee type is not known at the time of export</div></div>`;

const usppiNote = `<div style="margin:10px"><div>USPPI - U.S. Principal Party in Interest</div><br />
<div>If sale is a routed export, customer will need to contact the Freight Forwarder with a written authorization or a Power of Attorney (POA).</div></div>`;

export const COMMERCIAL_INVOICE_FORM = "commercialInvoice";
export const REPLACE_PDF = "replace_pdf";

export function Form(props) {
  return (
    <AppForm {...props}>
      <AppForm.Body>
        {["true", true].includes(props.getField(HAZMAT)) && (
          <Warning>Warning! This part is marked as Hazmat.</Warning>
        )}
        <FormRow>
          <Col md={3}>
            <Field
              name={EIN}
              label={EIN}
              component={FormInput}
              required
              disabled={!props.discreet}
            />
          </Col>
          <Col md={3}>
            <Field
              component={Select}
              options={options}
              onChange={(ev, val) => val && props.setFieldValue(USPPI)(val)}
              name={ROUTED_EXPORT}
              label={properCase(ROUTED_EXPORT)}
              required
            />
          </Col>
          <Col md={3}>
            <Field
              component={Select}
              options={options}
              name={HAZMAT}
              label={properCase(HAZMAT)}
              prompt={false}
              required
            />
          </Col>
          <Col md={3}>
            <span style={{ float: "right" }}>
              <NoteTooltip
                note={consigneeNote}
                placement="right"
                icon="fa-info-circle"
                color="rgba(255, 255, 255, 0.25)"
              />
            </span>
            <Field
              name={CONSIGNEE}
              label="Consignee Type"
              component={Select}
              disabled={props.repairOrderId}
              options={
                props.salesOrderId
                  ? toOptions(CONSIGNEE_TYPES)
                  : toOptions(REPAIR_ORDER_CONSIGNEE_TYPES)
              }
              required
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col md={3}>
            <Field
              name={CUSTOMER_PO}
              label="Cust PO#"
              component={FormInput}
              required
            />
          </Col>
          <InvoiceWeight label="Package Gross Weight" />
        </FormRow>
        <FormRow>
          <Col md={3}>
            <Field
              label={properCase(CARGO_SCREENING_CONSENT)}
              name={CARGO_SCREENING_CONSENT}
              component={Select}
              options={options}
              required
            />
          </Col>
          <Col md={3}>
            <span style={{ float: "right" }}>
              <NoteTooltip
                note={usppiNote}
                placement="right"
                icon="fa-info-circle"
                color="rgba(255, 255, 255, 0.25)"
              />
            </span>
            <Field
              name={USPPI}
              label={USPPI.toUpperCase()}
              component={Select}
              options={usppiOptions(
                [true, "true"].includes(props.getField(ROUTED_EXPORT))
              )}
              required
            />
          </Col>
        </FormRow>
        <hr className="hr-sm" />
        <h4>Freight Forwarder</h4>
        <FreightForwarderAddressFields
          path="freight_forwarder_address"
          formName={COMMERCIAL_INVOICE_FORM}
          freightForwarder={get(
            props,
            "initialValues.freight_forwarder_address"
          )}
          country={props.getField("freight_forwarder_address.country")}
          required
        />
        <FormRow>
          <Col md={3}>
            <Field
              name="freight_forwarder_reference"
              label="Ref#"
              component={FormInput}
            />
          </Col>
        </FormRow>
        <hr className="hr-sm" />
        {RESTRICTED_COUNTRIES.some(
          f => f.code === props.getField("ultimate_consignee_address.country")
        ) && (
          <Warning>
            This Country is on the embargoed list. Licensing will be requried to
            continue. Please contact Logistics Director or Export Compliance
            Officer prior to processing of Commercial Invoice.
          </Warning>
        )}
        <h4>Ultimate Consignee</h4>
        <AddressFields
          path="ultimate_consignee_address"
          formName="CommercialInvoiceForm"
          country={props.getField("ultimate_consignee_address.country")}
          addCompanyName
          required
        />
        <Contact path="ultimate_consignee" />
        <FormRow>
          <Col md={3}>
            <Field
              name="ultimate_consignee_reference"
              label="Ref#"
              component={FormInput}
            />
          </Col>
        </FormRow>
        <hr className="hr-sm" />
        <FormRow>
          <Col md={12}>
            <Field
              name={EXTERNAL_NOTE}
              component={TextArea}
              label={properCase(EXTERNAL_NOTE)}
              rows="5"
              cols="50"
              maxLength={230}
            />
          </Col>
        </FormRow>
        <hr className="hr-sm" />
        <h4>Parts</h4>
        <FieldArray
          name={LINE_ITEMS}
          component={PartDetails}
          getField={props.getField}
        />
      </AppForm.Body>
      <AppForm.Footer>
        {props.submitFailed && <Errors errors={props.errorMessages} />}
        <AppForm.Cancel
          close={() =>
            (props.setCurrentForm &&
              props.setCurrentForm(SHIPPING_CHECKLIST)) ||
            props.close()}
          editable
        />
        <Save
          formName={COMMERCIAL_INVOICE_FORM}
          {...props}
          salesOrderId={props.salesOrderId}
        />
        <Button type="submit" bsStyle="success">
          Download
        </Button>
      </AppForm.Footer>
    </AppForm>
  );
}

Form.propTypes = {};

const selector = formValueSelector(COMMERCIAL_INVOICE_FORM);

export default connect(
  state => ({
    getField: field => selector(state, field)
  }),
  (dispatch, ownProps) => ({
    onSubmit: async data => {
      if (
        ownProps.salesOrderId &&
        !ownProps.outbound &&
        data.has_commercial_invoice_attachment
      ) {
        ownProps.setCurrentForm &&
          ownProps.setCurrentForm(SAVE_OR_REPLACE_FORM);
      } else {
        const { data: invoice } = await api.commercialInvoices.create(data);
        window.location.href = `${API_BASE}/commercial_invoices/${invoice.id}/download_commercial_invoice`;
        ownProps.setCurrentForm
          ? ownProps.setCurrentForm(SHIPPING_CHECKLIST)
          : ownProps.close();

        ownProps.resetForm();
      }

      return Promise.resolve();
    },
    setFieldValue: field => val =>
      dispatch(change(COMMERCIAL_INVOICE_FORM, field, val))
  })
)(
  form({
    form: COMMERCIAL_INVOICE_FORM,
    validate
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/shared/components/CommercialInvoice/Form/Form.jsx